import { useDispatch, useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { renderRevision } from '../../../../shared/helpers/renderLocator';
import Locator from '../../../../shared/Locator';
import { useIsQualityEnabled } from '../../../helpers/features/quality';
import * as PackageInventoryStore from '../../../redux/packageInventory';
import { nuc } from '../../../utils';
import Flex from '../../generic/Flex';
import Checkbox from '../../generic/Forms/Checkbox';
import List from '../../generic/List';
import PackageLabelsList from '../../shared/packageLabels/PackageLabelsList';
import { PackageInventoryLicenses } from '../shared';
import type { PackageLabelAssignmentWithName } from '../../../../shared/model-typings/packagelabelassignment';
import type { PackageDetails } from '../../../../shared/organizationPackages/response';
import type { ReduxStore } from '../../../redux/store';

import VulnSummary from './VulnSummary';

import css from './PackageDetailsTable.module.less';

/**
 * If there are no blocked projects, we simply display the count.
 * If some of the projects are blocked, we show the blocked count
 * beside the project count. If all are blocked, we display "all blocked"
 */
const getProjectCountText = (details: PackageDetails): number | `${number} (all blocked)` | `${number} (${number} blocked)` => {
  if (!details.blockedProjectCount) {
    return details.numberOfProjects;
  }
  if (details.numberOfProjects === details.blockedProjectCount) {
    return `${details.numberOfProjects} (all blocked)`;
  }
  return `${details.numberOfProjects} (${details.blockedProjectCount} blocked)`;
};

type Props = PackageDetails & {
  pathname: string;
  versionQueryString: string;
};

/**
 * PackageDetailsTableRow displays specific version info, including
 * the revision/semver tag, project count, licenses, and vulns.
 * It acts as a link to the version details page (/packages/:locator/:version/projects).
 */
const PackageDetailsTableRow: VoidFunctionComponent<Props> = ({
  pathname,
  versionQueryString,
  ...details
}) => {
  const dispatch = useDispatch();
  const { fetcher, revision } = Locator.parse(details.packageRevision);
  const isSelected = useSelector((state: ReduxStore) =>
    PackageInventoryStore.selectors.getIsVersionSelected(state, details.packageRevision));
  const isVulnSummaryEnabled = fetcher !== 'csbinary';

  //  The package denial workflow is applied to quality policies, so they must have this enabled.
  const isQualityEnabled = useIsQualityEnabled();
  const body = (
    <>
      <div className={css.versionSummaryContainer}>
        <Flex justify="space-between">
          <List.RowTitle>{details.semverTag || renderRevision(details.packageRevision)}</List.RowTitle>
          { isVulnSummaryEnabled && <VulnSummary packageDetails={details} /> }
        </Flex>
      </div>
      <List.RowDetails>
        <List.RowDetailsItem
          trailingSlot={<List.RowDetailsIconItemBadge>{getProjectCountText(details)}</List.RowDetailsIconItemBadge>}
        >
          Projects
        </List.RowDetailsItem>
        <PackageInventoryLicenses licenses={details.licenses} />
        {(details.packageLabels && details.packageLabels.length > 0) && (
          <PackageLabelsList labels={details.packageLabels as PackageLabelAssignmentWithName[]} />
        )}
      </List.RowDetails>
    </>
  );

  return (
    <List.RowContainer>
      <List.Row
        to={{
          pathname: `${pathname}${revision ? `/${nuc(revision)}/projects` : ''}`,
          search: `?${versionQueryString}`,
        }}
        key={`${pathname}/${revision}`}
        flexDirection={isQualityEnabled ? 'row' : 'column'}
        hasCheckbox={isQualityEnabled}
        testId="package-inventory-package-table-row--versions"
    >
        {!isQualityEnabled ? body : (
          <>
            <Checkbox
              isChecked={isSelected}
              onChange={() => dispatch(PackageInventoryStore.actions.toggleVersionSelected(details.packageRevision))}
              isRippleDisabled
              shouldStopPropagation
            />
            <div className="width-full padding-left-sm padding-right-sm">
              {body}
            </div>
          </>
        )}
      </List.Row>
    </List.RowContainer>
  );
};

export default PackageDetailsTableRow;
